import * as React from 'react';

import type { EstiloComToken } from '@/design/estilo';
import { cn } from '@/lib/utils';

/* Celula — o bloco de que toda tela é feita.
 *
 * Substitui o "cartão branco com sombra". Aqui não existe branco, não existe
 * sombra e não existe borda cinza: o que separa duas células é o CAMPO DE COR
 * de cada uma e a parede de cera de 8px entre elas.
 *
 * O campo carrega significado, e escolher campo é uma decisão de conteúdo:
 *
 *   mel    o que SAI      custo, despesa, ponto de equilíbrio
 *   mata   o que FICA     veredito e decisão. No máximo 2× por tela
 *   mint   informação     neutro, sem julgamento
 *   areia  inerte         contexto, fato que não opina
 *   telha  perda          prejuízo
 *   nata   leitura        texto e formulário (o padrão)
 *   creme  o chão         raro dentro de uma tela; é a cor do fundo
 *
 * Dentro de `campo="mata"` a tinta inteira se inverte sozinha, inclusive a
 * dos componentes do shadcn: botão, selo e separador acertam sem que a tela
 * precise passar classe nenhuma. Quem faz isso é o seletor [data-campo] em
 * tokens.css e globals.css — não há prop de "modo escuro" para esquecer. */

export type CampoDeCor = 'creme' | 'nata' | 'mel' | 'mint' | 'areia' | 'telha' | 'mata';

/** Respiro interno. Ele também define o raio dos blocos aninhados. */
export type RespiroCelula = 'nenhum' | 'justo' | 'padrao' | 'folgado';

/** Tags permitidas. Célula é um contêiner de bloco, nunca um controle. */
export type TagCelula = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'li';

/* Tipado por HTMLAttributes (e não por ComponentProps<'div'>) de propósito:
 * `como` troca a tag, e ComponentProps prenderia o tipo do ref ao HTMLDivElement,
 * fazendo `como="li"` não compilar. */
export type CelulaProps = React.HTMLAttributes<HTMLElement> & {
  campo?: CampoDeCor | undefined;
  respiro?: RespiroCelula | undefined;
  /** Elemento renderizado. Use `section` quando a célula tiver título próprio. */
  como?: TagCelula | undefined;
  /** A célula assenta ao entrar: sobe 10px e encaixa. */
  assenta?: boolean | undefined;
  /** Atraso do assentamento, em ms. É a tela que conta a própria ordem. */
  atraso?: number | undefined;
};

export function Celula({
  campo = 'nata',
  respiro = 'padrao',
  como = 'div',
  assenta = false,
  atraso,
  className,
  style,
  ...resto
}: CelulaProps) {
  const Tag = como as React.ElementType;
  return (
    <Tag
      data-campo={campo}
      data-respiro={respiro}
      {...(assenta ? { 'data-assenta': '' } : {})}
      className={cn('celula', className)}
      style={atraso === undefined ? style : ({ ...style, '--atraso': `${atraso}ms` } as EstiloComToken)}
      {...resto}
    />
  );
}

/* Camada — o bloco aninhado dentro de uma célula.
 *
 * Duas coisas acontecem sozinhas e são o motivo de este componente existir:
 *
 * 1. RAIO CONCÊNTRICO. O raio da camada é o da célula MENOS o respiro dela,
 *    calculado em CSS. Mudar o respiro do pai corrige o canto do filho sem
 *    ninguém tocar em nada. É o detalhe que faz um canto parecer certo em vez
 *    de quase certo, e é o que quase todo sistema erra.
 * 2. PROFUNDIDADE POR COR. A camada é uma tinta do próprio pai (5,5% de tinta
 *    dentro do claro, 7% de branco dentro do mata). Nunca uma sombra. */
export type CamadaProps = React.HTMLAttributes<HTMLElement> & { como?: TagCelula | undefined };

export function Camada({ como = 'div', className, ...resto }: CamadaProps) {
  const Tag = como as React.ElementType;
  return <Tag className={cn('camada', className)} {...resto} />;
}
